import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase'
import { useAuth } from '../../contexts/AuthContext'
import { TAUX_TVA, TAUX_REMUNERATION, SEUIL_MINIMUM, formatEuro } from '../../utils/calculs'
import './Saisie.css'
import ImportCSV from './ImportCSV'
import { tarifAccueilPour } from '../../utils/tarifsJournee'
import { aujourdhui } from '../../utils/planning'
import Combobox from '../../components/Combobox'

const HEURES_JOURNEE = 10

export default function Saisie() {
  const { profile } = useAuth()
  const [pole, setPole] = useState('SPA')
  const [date, setDate] = useState(aujourdhui())

  // SPA/HYDRO state
  const [superviseurId, setSupId] = useState('')
  const [formData, setFormData] = useState({})
  const [tarifs, setTarifs] = useState([])
  const [seanceDuJour, setSeanceDuJour] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [partageHoraire, setPartageHoraire] = useState(false)
  const [sup2Id, setSup2Id] = useState('')
  const [sup1Heures, setSup1Heures] = useState(8)
  const [sup2Heures, setSup2Heures] = useState(2)

  // ACCUEIL state
  const [accueilLignes, setAccueilLignes] = useState([{ superviseurId: '', montant: 0, nb_heures: '', taux_horaire: '', modeHoraire: false }])
  const [accueilExistant, setAccueilExistant] = useState([])
  const [accueilEditMode, setAccueilEditMode] = useState(true)

  // Commun
  const [superviseurs, setSuperviseurs] = useState([])
  const [tarifAccueil, setTarifAccueil] = useState(0)
  const [accueilMajore, setAccueilMajore] = useState(false)
  const [tarifAccueilManquant, setTarifAccueilManquant] = useState(false)
  const [tarifsLoading, setTarifsLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [msg, setMsg] = useState(null)

  useEffect(() => {
    supabase.from('profiles').select('id, prenom, nom')
      .eq('role', 'superviseur').eq('actif', true).order('prenom')
      .then(({ data }) => setSuperviseurs(data || []))
  }, [])

  const loadTarifs = useCallback(async () => {
    if (pole === 'ACCUEIL') { setTarifsLoading(false); return }
    setTarifsLoading(true)
    const { data } = await supabase.from('tarifs').select('*')
      .eq('pole', pole).eq('actif', true)
      .lte('date_debut', date).or(`date_fin.is.null,date_fin.gte.${date}`)
      .order('prix', { ascending: false })
    setTarifs(data || [])
    const init = {}
    for (const t of data || []) init[t.id] = 0
    setFormData(init)
    setTarifsLoading(false)
  }, [pole, date])

  useEffect(() => { loadTarifs() }, [loadTarifs])

  // Tarif d'accueil du jour (standard ou majore), lu dans tarifs_journee
  useEffect(() => {
    let annule = false
    tarifAccueilPour(date).then(({ montant, majore }) => {
      if (annule) return
      setAccueilMajore(majore)
      setTarifAccueilManquant(montant === null)
      setTarifAccueil(montant === null ? 0 : montant)
    })
    return () => { annule = true }
  }, [date])

  // Une journee pas encore saisie prend le tarif une fois celui-ci charge
  useEffect(() => {
    if (accueilExistant.length > 0) return
    setAccueilLignes(prev => prev.map(l => (l.modeHoraire ? l : { ...l, montant: tarifAccueil })))
  }, [tarifAccueil, accueilExistant])

  useEffect(() => {
    if (!date) return
    setMsg(null)
    async function loadSeance() {
      if (pole === 'ACCUEIL') {
        const { data } = await supabase.from('seances_accueil')
          .select('*, superviseur:superviseur_id(prenom, nom)')
          .eq('date', date).order('created_at')
        if (data && data.length > 0) {
          setAccueilExistant(data)
          setAccueilLignes(data.map(d => ({
            id: d.id,
            superviseurId: d.superviseur_id,
            montant: d.montant,
            nb_heures: d.nb_heures || '',
            taux_horaire: d.taux_horaire || '',
            modeHoraire: !!d.nb_heures
          })))
          setAccueilEditMode(false)
        } else {
          setAccueilExistant([])
          setAccueilLignes([{ superviseurId: '', montant: tarifAccueil, nb_heures: '', taux_horaire: '', modeHoraire: false }])
          setAccueilEditMode(true)
        }
      } else {
        const { data: seance } = await supabase.from('seances')
          .select('*').eq('date', date).eq('pole', pole).maybeSingle()
        if (seance) {
          setSeanceDuJour(seance)
          setSupId(seance.superviseur_id || '')
          setPartageHoraire(seance.partage_horaire || false)
          setSup2Id(seance.superviseur2_id || '')
          setSup1Heures(seance.sup1_heures || 8)
          setSup2Heures(seance.sup2_heures || 2)
          setEditMode(false)
          const { data: soins } = await supabase.from('seance_soins').select('*').eq('seance_id', seance.id)
          const q = {}
          for (const t of tarifs) q[t.id] = 0
          for (const s of soins || []) { if (q[s.tarif_id] !== undefined) q[s.tarif_id] = s.quantite }
          setFormData(q)
        } else {
          setSeanceDuJour(null)
          setEditMode(true)
          setSupId('')
          setPartageHoraire(false)
          setSup2Id('')
          setSup1Heures(8)
          setSup2Heures(2)
          const init = {}
          for (const t of tarifs) init[t.id] = 0
          setFormData(init)
        }
      }
    }
    loadSeance()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, pole, tarifs.length])

  // Calculs SPA/HYDRO
  const tarifsPayants = tarifs.filter(t => t.prix > 0)
  const tarifsGratuits = tarifs.filter(t => t.prix === 0)
  const ca_ttc = tarifs.reduce((a, t) => a + (formData[t.id] || 0) * t.prix, 0)
  const ca_ht = parseFloat((ca_ttc / TAUX_TVA).toFixed(2))
  const total_payant = tarifsPayants.reduce((a, t) => a + (formData[t.id] || 0), 0)
  const total_gratuit = tarifsGratuits.reduce((a, t) => a + (formData[t.id] || 0), 0)
  const total_massage = total_payant + total_gratuit
  // Portes ouvertes : soins gratuits (prix 0, hors CA) mais valorisés pour la rému du superviseur via prix_reference
  const nbPortesOuvertes = tarifsGratuits.reduce((a, t) => a + (t.prix_reference ? (formData[t.id] || 0) : 0), 0)
  const valorisationPortesOuvertes = tarifsGratuits.reduce((a, t) => a + (formData[t.id] || 0) * (t.prix_reference || 0), 0)
  const base_remuneration_ht = parseFloat(((ca_ttc + valorisationPortesOuvertes) / TAUX_TVA).toFixed(2))
  const remuneration_reelle = parseFloat((base_remuneration_ht * TAUX_REMUNERATION).toFixed(2))
  const remuneration_affichee = Math.round(Math.max(remuneration_reelle, SEUIL_MINIMUM))
  const tauxHoraire = parseFloat((remuneration_affichee / HEURES_JOURNEE).toFixed(2))
  const sup1Rem = partageHoraire ? Math.round(tauxHoraire * sup1Heures) : remuneration_affichee
  const sup2Rem = partageHoraire ? Math.round(tauxHoraire * sup2Heures) : 0

  function handleChange(tarifId, val) {
    setFormData(prev => ({ ...prev, [tarifId]: Math.max(0, parseInt(val) || 0) }))
  }

  // Accueil helpers
  function updateLigne(idx, field, val) {
    setAccueilLignes(prev => prev.map((l, i) => {
      if (i !== idx) return l
      const updated = { ...l, [field]: val }
      if (field === 'modeHoraire' && val) {
        updated.taux_horaire = tarifAccueil / HEURES_JOURNEE
      }
      if ((field === 'nb_heures' || field === 'taux_horaire') && updated.modeHoraire) {
        const h = parseFloat(updated.nb_heures) || 0
        const tx = parseFloat(updated.taux_horaire) || 0
        updated.montant = Math.round(h * tx)
      }
      return updated
    }))
  }

  function ajouterLigne() {
    setAccueilLignes(prev => [...prev, { superviseurId: '', montant: tarifAccueil, nb_heures: '', taux_horaire: '', modeHoraire: false }])
  }

  function supprimerLigne(idx) {
    setAccueilLignes(prev => prev.filter((_, i) => i !== idx))
  }

  async function handleSaveAccueil() {
    for (const l of accueilLignes) {
      if (!l.superviseurId) return setMsg({ type: 'error', text: 'Veuillez sélectionner un superviseur pour chaque ligne.' })
    }
    // Vérifier doublons
    const ids = accueilLignes.map(l => l.superviseurId)
    if (new Set(ids).size !== ids.length) return setMsg({ type: 'error', text: 'Un superviseur ne peut pas apparaître deux fois.' })

    setLoading(true)
    setMsg(null)

    // Supprimer les anciennes lignes
    if (accueilExistant.length > 0) {
      await supabase.from('seances_accueil').delete().eq('date', date)
    }

    // Insérer les nouvelles
    const payload = accueilLignes.map(l => ({
      date,
      superviseur_id: l.superviseurId,
      montant: l.montant,
      nb_heures: l.nb_heures ? parseFloat(l.nb_heures) : null,
      taux_horaire: l.taux_horaire ? parseFloat(l.taux_horaire) : null,
      cree_par: profile.id
    }))

    const { error } = await supabase.from('seances_accueil').insert(payload)
    if (error) { setMsg({ type: 'error', text: error.message }) }
    else {
      setMsg({ type: 'success', text: 'Journée accueil enregistrée.' })
      const { data } = await supabase.from('seances_accueil')
        .select('*, superviseur:superviseur_id(prenom, nom)').eq('date', date)
      setAccueilExistant(data || [])
      setAccueilEditMode(false)
    }
    setLoading(false)
  }

  async function handleSave() {
    if (!superviseurId) return setMsg({ type: 'error', text: 'Veuillez sélectionner un superviseur.' })
    if (partageHoraire && !sup2Id) return setMsg({ type: 'error', text: 'Veuillez sélectionner le 2ème superviseur.' })
    setLoading(true)
    setMsg(null)

    const payload = {
      date, pole, superviseur_id: superviseurId,
      superviseur2_id: partageHoraire ? sup2Id : null,
      partage_horaire: partageHoraire,
      sup1_heures: partageHoraire ? sup1Heures : null,
      sup2_heures: partageHoraire ? sup2Heures : null,
      taux_horaire: partageHoraire ? tauxHoraire : null,
      sup1_remuneration: partageHoraire ? sup1Rem : null,
      sup2_remuneration: partageHoraire ? sup2Rem : null,
      cree_par: profile.id,
      total_massage_payant: total_payant, total_massage_gratuit: total_gratuit,
      total_massage, ca_ttc, ca_ht, remuneration_reelle, remuneration_affichee,
      nb_sde_40:0, nb_sde_35:0, nb_sde_33:0, nb_sde_30:0, nb_sde_29:0, nb_sde_20:0,
      nb_vip_gratuit:0, nb_sup:0, nb_eleves:0,
      nb_hydro_60:0, nb_hydro_55:0, nb_hydro_35:0, nb_hydro_33:0, nb_hydro_30:0,
      nb_hydro_29:0, nb_hydro_vip:0,
    }

    let seanceId
    if (seanceDuJour && editMode) {
      const { error } = await supabase.from('seances').update(payload).eq('id', seanceDuJour.id)
      if (error) { setMsg({ type: 'error', text: error.message }); setLoading(false); return }
      seanceId = seanceDuJour.id
      await supabase.from('seance_soins').delete().eq('seance_id', seanceId)
    } else {
      const { data, error } = await supabase.from('seances').insert(payload).select().single()
      if (error) { setMsg({ type: 'error', text: error.message }); setLoading(false); return }
      seanceId = data.id
    }

    const soinsPayload = tarifs.filter(t => (formData[t.id] || 0) > 0).map(t => ({
      seance_id: seanceId, tarif_id: t.id, label: t.label, prix: t.prix,
      quantite: formData[t.id], total: formData[t.id] * t.prix, gratuit: t.prix === 0
    }))
    if (soinsPayload.length > 0) await supabase.from('seance_soins').insert(soinsPayload)

    setMsg({ type: 'success', text: 'Séance enregistrée.' })
    const { data: updated } = await supabase.from('seances').select('*').eq('id', seanceId).single()
    setSeanceDuJour(updated)
    setEditMode(false)
    setLoading(false)
  }

  const isReadOnly = pole === 'ACCUEIL' ? !accueilEditMode : (seanceDuJour && !editMode)
  const isCloture = pole === 'ACCUEIL'
    ? accueilExistant.some(a => a.cloture)
    : seanceDuJour?.cloture

  return (
    <div>
      <div className="page-header">
        <h2>Saisie des séances</h2>
        <button className="btn btn-outline" onClick={() => setShowImport(true)}>⬆ Import Spa Booker</button>
      </div>
      {showImport && <ImportCSV onClose={() => setShowImport(false)} />}

      <div className="card" style={{marginBottom:20}}>
        <div className="saisie-controls-row">
          <div className="form-group" style={{margin:0}}>
            <label>Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="form-group" style={{margin:0}}>
            <label>Pôle</label>
            <select value={pole} onChange={e => { setPole(e.target.value); setMsg(null) }}>
              <option value="SPA">SPA</option>
              <option value="HYDRO">Hydrothérapie</option>
              <option value="ACCUEIL">Accueil</option>
            </select>
          </div>
          {pole !== 'ACCUEIL' && (
            <div className="form-group" style={{margin:0}}>
              <label>Superviseur</label>
              <Combobox
                value={superviseurId}
                onChange={setSupId}
                disabled={isReadOnly}
                placeholder="Choisir..."
                options={superviseurs.map(s => ({ value: s.id, label: `${s.prenom} ${s.nom}` }))}
              />
            </div>
          )}
        </div>
      </div>

      {isReadOnly && !isCloture && (
        <div className="alert alert-info" style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <span>Journée déjà saisie.</span>
          <button className="btn btn-outline btn-sm" onClick={() => pole === 'ACCUEIL' ? setAccueilEditMode(true) : setEditMode(true)}>Modifier</button>
        </div>
      )}
      {isCloture && <div className="alert alert-info" style={{display:'flex', justifyContent:'space-between'}}><span>Journée clôturée.</span><span className="badge badge-cloture">Clôturée</span></div>}

      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

      {pole === 'ACCUEIL' ? (
        <div className="saisie-grid">
          <div className="card">
            <h3 style={{marginBottom:20}}>Journée Accueil</h3>
            {accueilMajore && (
              <div className="alert alert-info">Jour majoré (férié ou samedi sans Anne-Julie) : tarif {formatEuro(tarifAccueil)}.</div>
            )}
            {tarifAccueilManquant && (
              <div className="alert alert-warning">Aucun tarif d'accueil trouvé pour cette date. Renseigne-le dans Soins &amp; Tarifs, onglet Journées.</div>
            )}
            {accueilLignes.map((ligne, idx) => (
              <div key={idx} style={{borderBottom:'1px solid var(--border-light)', paddingBottom:16, marginBottom:16}}>
                <div style={{display:'flex', gap:12, alignItems:'flex-end', flexWrap:'wrap'}}>
                  <div className="form-group" style={{margin:0, flex:2, minWidth:160}}>
                    <label>Superviseur {idx > 0 ? idx + 1 : ''}</label>
                    <Combobox
                      value={ligne.superviseurId}
                      onChange={v => updateLigne(idx, 'superviseurId', v)}
                      disabled={!accueilEditMode}
                      placeholder="Choisir..."
                      options={superviseurs.map(s => ({ value: s.id, label: `${s.prenom} ${s.nom}` }))}
                    />
                  </div>

                  <div className="form-group" style={{margin:0}}>
                    <label style={{display:'flex', alignItems:'center', gap:6, textTransform:'none', letterSpacing:0, fontSize:12}}>
                      <input type="checkbox" checked={ligne.modeHoraire}
                        onChange={e => updateLigne(idx, 'modeHoraire', e.target.checked)}
                        disabled={!accueilEditMode} />
                      Facturation horaire
                    </label>
                  </div>

                  {ligne.modeHoraire ? (
                    <>
                      <div className="form-group" style={{margin:0, width:90}}>
                        <label>Nb heures</label>
                        <input type="number" min="0" max="24" step="0.5"
                          value={ligne.nb_heures}
                          onChange={e => updateLigne(idx, 'nb_heures', e.target.value)}
                          disabled={!accueilEditMode} className="nb-input" />
                      </div>
                      <div className="form-group" style={{margin:0, width:110}}>
                        <label>Taux/heure (€)</label>
                        <input type="number" min="0" step="0.01"
                          value={ligne.taux_horaire}
                          onChange={e => updateLigne(idx, 'taux_horaire', e.target.value)}
                          disabled={!accueilEditMode} className="nb-input" />
                      </div>
                    </>
                  ) : (
                    <div className="form-group" style={{margin:0, width:130}}>
                      <label>Montant (€)</label>
                      <input type="number" min="0" step="0.01"
                        value={ligne.montant}
                        onChange={e => updateLigne(idx, 'montant', parseFloat(e.target.value) || 0)}
                        disabled={!accueilEditMode} className="nb-input" />
                    </div>
                  )}

                  {accueilEditMode && idx > 0 && (
                    <button className="btn btn-danger-ghost btn-sm" onClick={() => supprimerLigne(idx)}>✕</button>
                  )}
                </div>
                <div style={{fontSize:12, color:'var(--stone)', marginTop:8}}>
                  Montant : <strong>{formatEuro(ligne.montant)}</strong>
                </div>
              </div>
            ))}

            {accueilEditMode && (
              <button className="btn btn-outline btn-sm" onClick={ajouterLigne} style={{marginTop:4}}>
                + Ajouter un superviseur
              </button>
            )}
          </div>

          <div className="card">
            <h3 style={{marginBottom:20}}>Récapitulatif</h3>
            {accueilLignes.map((l, idx) => {
              const sup = superviseurs.find(s => s.id === l.superviseurId)
              return (
                <div key={idx} className="recap-line">
                  <span>{sup ? `${sup.prenom} ${sup.nom}` : `Superviseur ${idx + 1}`}</span>
                  <strong>{formatEuro(l.montant)}</strong>
                </div>
              )
            })}
            <div className="recap-highlight" style={{marginTop:8}}>
              <div className="recap-line" style={{border:'none', padding:0}}>
                <span>Total à facturer</span>
                <strong>{formatEuro(accueilLignes.reduce((a, l) => a + (l.montant || 0), 0))}</strong>
              </div>
            </div>
            {accueilEditMode && !isCloture && (
              <button className="btn btn-bordeaux" style={{width:'100%', marginTop:20}}
                onClick={handleSaveAccueil} disabled={loading}>
                {loading ? 'Enregistrement...' : 'Enregistrer la journée'}
              </button>
            )}
          </div>
        </div>
      ) : (
        tarifsLoading ? <div className="spinner" /> : (
          <>
            {/* Option partage horaire */}
            {!isReadOnly && (
              <div className="card" style={{marginBottom:16, padding:'16px 20px'}}>
                <label style={{display:'flex', alignItems:'center', gap:10, cursor:'pointer', fontSize:13}}>
                  <input type="checkbox" checked={partageHoraire}
                    onChange={e => { setPartageHoraire(e.target.checked); if (!e.target.checked) { setSup2Id(''); setSup1Heures(8); setSup2Heures(2) } }} />
                  <span><strong>Partage horaire exceptionnel</strong> — deux superviseurs sur cette journée</span>
                </label>
                {partageHoraire && (
                  <div style={{marginTop:16, display:'grid', gridTemplateColumns:'1fr 80px 1fr 80px', gap:12, alignItems:'end'}}>
                    <div className="form-group" style={{margin:0}}>
                      <label>2ème superviseur</label>
                      <Combobox
                        value={sup2Id}
                        onChange={setSup2Id}
                        placeholder="Choisir..."
                        options={superviseurs.filter(s => s.id !== superviseurId).map(s => ({ value: s.id, label: `${s.prenom} ${s.nom}` }))}
                      />
                    </div>
                    <div className="form-group" style={{margin:0}}>
                      <label>H. sup. 1</label>
                      <input type="number" min="0" max="10" step="0.5" value={sup1Heures}
                        onChange={e => setSup1Heures(parseFloat(e.target.value) || 0)} className="nb-input" />
                    </div>
                    <div className="form-group" style={{margin:0}}>
                      <label style={{fontSize:11, color:'var(--stone)'}}>
                        Taux horaire : {formatEuro(tauxHoraire)}/h (rém. ÷ {HEURES_JOURNEE})
                      </label>
                      <div style={{fontSize:13, padding:'9px 0', color:'var(--charcoal)'}}>
                        {superviseurs.find(s => s.id === superviseurId)?.prenom || 'Sup. 1'} : {formatEuro(sup1Rem)} · {superviseurs.find(s => s.id === sup2Id)?.prenom || 'Sup. 2'} : {formatEuro(sup2Rem)}
                      </div>
                    </div>
                    <div className="form-group" style={{margin:0}}>
                      <label>H. sup. 2</label>
                      <input type="number" min="0" max="10" step="0.5" value={sup2Heures}
                        onChange={e => setSup2Heures(parseFloat(e.target.value) || 0)} className="nb-input" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Vue lecture partage horaire */}
            {isReadOnly && seanceDuJour?.partage_horaire && (
              <div className="alert alert-info" style={{marginBottom:16}}>
                Partage horaire — {superviseurs.find(s => s.id === seanceDuJour.superviseur_id)?.prenom} ({seanceDuJour.sup1_heures}h : {formatEuro(seanceDuJour.sup1_remuneration)}) &
                {' '}{superviseurs.find(s => s.id === seanceDuJour.superviseur2_id)?.prenom} ({seanceDuJour.sup2_heures}h : {formatEuro(seanceDuJour.sup2_remuneration)})
              </div>
            )}

            <div className="saisie-grid">
              <div className="card">
                <h3 style={{marginBottom:20}}>Soins {pole}</h3>
                {tarifs.length === 0 ? (
                  <div className="alert alert-warning">Aucun soin actif pour ce pôle.</div>
                ) : (
                  <table>
                    <thead>
                      <tr><th>Soin</th><th className="num">Prix unitaire</th><th>Quantité</th><th className="num">Total</th></tr>
                    </thead>
                    <tbody>
                      {tarifsPayants.map(t => (
                        <tr key={t.id}>
                          <td>{t.label}</td><td className="num">{t.prix} €</td>
                          <td><input type="number" min="0" value={formData[t.id] || 0}
                            onChange={e => handleChange(t.id, e.target.value)}
                            disabled={isReadOnly} className="nb-input" /></td>
                          <td className="num">{formatEuro((formData[t.id] || 0) * t.prix)}</td>
                        </tr>
                      ))}
                      {tarifsGratuits.map(t => (
                        <tr key={t.id} style={{color:'var(--stone)', fontStyle:'italic'}}>
                          <td>{t.label}</td><td className="num">Gratuit</td>
                          <td><input type="number" min="0" value={formData[t.id] || 0}
                            onChange={e => handleChange(t.id, e.target.value)}
                            disabled={isReadOnly} className="nb-input" /></td>
                          <td className="num text-muted">—</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="card">
                <h3 style={{marginBottom:20}}>Récapitulatif</h3>
                <div className="recap-line"><span>Soins payants</span><strong>{total_payant}</strong></div>
                <div className="recap-line"><span>Soins gratuits</span><strong>{total_gratuit}</strong></div>
                <div className="divider" />
                <div className="recap-line"><span>CA TTC</span><strong>{formatEuro(ca_ttc)}</strong></div>
                <div className="recap-line"><span>CA HT</span><strong>{formatEuro(ca_ht)}</strong></div>
                {nbPortesOuvertes > 0 && (
                  <div className="recap-line" style={{fontStyle:'italic', color:'var(--stone)'}}>
                    <span>Dont {nbPortesOuvertes} portes ouvertes (valorisées {formatEuro(valorisationPortesOuvertes)} pour le sup.)</span>
                  </div>
                )}
                <div className="divider" />
                {partageHoraire ? (
                  <>
                    <div className="recap-line">
                      <span>{superviseurs.find(s => s.id === superviseurId)?.prenom || 'Sup. 1'} ({sup1Heures}h)</span>
                      <strong style={{color:'var(--bordeaux)'}}>{formatEuro(sup1Rem)}</strong>
                    </div>
                    <div className="recap-line">
                      <span>{superviseurs.find(s => s.id === sup2Id)?.prenom || 'Sup. 2'} ({sup2Heures}h)</span>
                      <strong style={{color:'var(--bordeaux)'}}>{formatEuro(sup2Rem)}</strong>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="recap-line"><span>Rém. réelle (35,20% HT)</span><strong>{formatEuro(remuneration_reelle)}</strong></div>
                    <div className="recap-highlight">
                      <div className="recap-line" style={{border:'none', padding:0}}>
                        <span>Rém. à facturer</span>
                        <strong>{formatEuro(remuneration_affichee)}</strong>
                      </div>
                    </div>
                    {remuneration_reelle < SEUIL_MINIMUM && (
                      <div style={{fontSize:11, color:'var(--stone)', marginTop:8}}>Seuil minimum de {SEUIL_MINIMUM} € appliqué</div>
                    )}
                  </>
                )}
                {!isReadOnly && (
                  <button className="btn btn-bordeaux" style={{width:'100%', marginTop:20}}
                    onClick={handleSave} disabled={loading || tarifs.length === 0}>
                    {loading ? 'Enregistrement...' : 'Enregistrer la séance'}
                  </button>
                )}
              </div>
            </div>
          </>
        )
      )}
    </div>
  )
}
